
import React, { Component } from 'react';
import alertify from 'alertifyjs';
import service from '../../services';
import commonImage from '../../commonImage';
import DeletefileIcon from '../../icons/DeletefileIcon';
import UploadForm from '../UploadForm';

const HEX_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
const HEX6_REGEX = /^#[0-9A-Fa-f]{6}$/;

const HUB_PAGES = {
    HOME: 'HOME',
    PROGRAM: 'PROGRAM',
    MY_FEST: 'MY_FEST',
    GUIDES: 'GUIDES',
    MORE: 'MORE',
};

const HUB_PAGE_LABELS = {
    HOME: 'Home',
    PROGRAM: 'Program',
    MY_FEST: 'My Fest',
    GUIDES: 'Guides',
    MORE: 'More',
};

const HUB_PAGES_OPTIONS = Object.keys(HUB_PAGES).map(key => ({
    value: HUB_PAGES[key],
    label: HUB_PAGE_LABELS[key],
}));

class AppConfigForm extends Component {
    constructor(props) {
        super(props);
        this.apiserve = new service();
        this.sub = null;
        this.saveTimeout = null;
        this.state = {
            loaderFlag: false,
            selectedHubPage: HUB_PAGES.PROGRAM,
            configData: [],
            selectedType: 'default',
            imageFile: null,
            imagePreview: null,
            imageUploading: false,
            hexColor: '',
            hexError: '',
            savedType: '',
            savedValue: '',
            isDirty: false,
            configLoaded: false,
            adPerScreenings: '',
            savedAdPerScreenings: '',
            adPerScreeningsError: '',
        };
        this.handleHubPageChange = this.handleHubPageChange.bind(this);
        this.handleTypeChange = this.handleTypeChange.bind(this);
        this.handleImageChange = this.handleImageChange.bind(this);
        this.handleHexChange = this.handleHexChange.bind(this);
        this.handleColorPickerChange = this.handleColorPickerChange.bind(this);
        this.handleImageDelete = this.handleImageDelete.bind(this);
        this.handleSave = this.handleSave.bind(this);
        this.handleReset = this.handleReset.bind(this);
        this.handleAdPerScreeningsChange = this.handleAdPerScreeningsChange.bind(this);
    }

    componentDidMount = () => {
        this.sub = this.apiserve.getevent().subscribe(response => {
            this.handleSocketResponse(response);
        });
        this.apiserve.sendevent('mobileAppConfiguration|getConfig', {});
    }

    componentWillUnmount = () => {
        if (this.sub) this.sub.unsubscribe();
        if (this.saveTimeout) clearTimeout(this.saveTimeout);
    }

    getPageEntry(configData, hubPage) {
        return (configData || []).find(item => item.hubPage === hubPage) || null;
    }

    extractPageState(entry) {
        const { hubscreenBackground, updatedAt, updatedBy } = (entry || {});
        const { type, value } = (hubscreenBackground || {});
        return {
            savedType: type || 'default',
            savedValue: value || '',
            selectedType: type || 'default',
            hexColor: type === 'color' ? (value || '') : '',
            hexError: '',
            imageFile: null,
            imagePreview: type === 'image' ? (value || null) : null,
            isDirty: false,
            configLoaded: true,
            _updatedAt: updatedAt || null,
            _updatedBy: updatedBy || null,
        };
    }

    handleSocketResponse(response) {
        if (!response || !response.event) return;

        if (response.event === 'mobileAppConfiguration|getConfig') {
            const responseData = (response.data || {});
            const configData = (responseData.mobileAppConfiguration || []);
            const adPerScreenings = responseData.adPerScreenings !== undefined
                ? String(responseData.adPerScreenings)
                : '';
            const { selectedHubPage } = this.state;
            const entry = this.getPageEntry(configData, selectedHubPage);
            const pageState = this.extractPageState(entry);
            this.setState({
                configData,
                adPerScreenings,
                savedAdPerScreenings: adPerScreenings,
                adPerScreeningsError: '',
                ...pageState,
            });
            if (this.props.onLastUpdated) {
                this.props.onLastUpdated(pageState._updatedAt, pageState._updatedBy);
            }
        }

        if (response.event === 'mobileAppConfiguration|updateConfig') {
            if (this.saveTimeout) {
                clearTimeout(this.saveTimeout);
                this.saveTimeout = null;
            }
            this.setState({ loaderFlag: false, imageUploading: false }, () => {
                alertify.success('App configuration saved successfully.');
                this.apiserve.sendevent('mobileAppConfiguration|getConfig', {});
            });
        }
    }

    checkDirty(newState) {
        if (!newState.configLoaded) return false;
        const { selectedType, imageFile, hexColor, savedType, savedValue, adPerScreenings, savedAdPerScreenings } = newState;
        if (selectedType !== savedType) return true;
        if (selectedType === 'image' && imageFile !== null) return true;
        if (selectedType === 'color' && hexColor !== savedValue) return true;
        if (adPerScreenings !== savedAdPerScreenings) return true;
        return false;
    }

    handleHubPageChange(e) {
        const hubPage = e.target.value;
        const { configData } = this.state;
        const entry = this.getPageEntry(configData, hubPage);
        const pageState = this.extractPageState(entry);
        this.setState({
            selectedHubPage: hubPage,
            ...pageState,
        });
        if (this.props.onLastUpdated) {
            this.props.onLastUpdated(pageState._updatedAt, pageState._updatedBy);
        }
    }

    handleTypeChange(type) {
        this.setState(prevState => {
            const restoredPreview = type === 'image' && prevState.savedType === 'image'
                ? (prevState.savedValue || null)
                : null;
            const next = {
                ...prevState,
                selectedType: type,
                hexError: '',
                imageFile: null,
                imagePreview: restoredPreview,
            };
            return { ...next, isDirty: this.checkDirty(next) };
        });
    }

    handleImageChange(e) {
        const file = e.target.files[0];
        if (!file) return;

        const sizeKB = Math.round(file.size / 1024);
        if (sizeKB >= 1024) {
            alertify.error('Please upload an image smaller than 1 MB');
            return;
        }

        const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
        if (!allowedTypes.includes(file.type)) {
            alertify.error('Please upload PNG, JPG or JPEG only');
            return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = (event) => {
            this.setState({ imageFile: file, imagePreview: event.target.result }, () => {
                this.setState({ isDirty: this.checkDirty(this.state) });
            });
        };
    }

    handleImageDelete() {
        this.setState(prevState => ({
            imageFile: null,
            imagePreview: null,
            isDirty: this.checkDirty({ ...prevState, imageFile: null, imagePreview: null }),
        }));
    }

    handleHexChange(e) {
        const hexColor = e.target.value;
        this.setState(prevState => {
            const next = { ...prevState, hexColor, hexError: '' };
            return { ...next, isDirty: this.checkDirty(next) };
        });
    }

    handleColorPickerChange(e) {
        const hexColor = e.target.value.toUpperCase();
        this.setState(prevState => {
            const next = { ...prevState, hexColor, hexError: '' };
            return { ...next, isDirty: this.checkDirty(next) };
        });
    }

    async handleSave() {
        const {
            selectedType, imageFile, hexColor, imagePreview, selectedHubPage,
            adPerScreenings, savedAdPerScreenings,
        } = this.state;

        if (adPerScreenings !== savedAdPerScreenings) {
            const raw = adPerScreenings.trim();
            const val = parseInt(raw, 10);
            if (!raw || isNaN(val) || val < 1 || val > 30) {
                this.setState({ adPerScreeningsError: 'Value must be between 1 and 30.' });
                return;
            }
        }

        const adsVal = parseInt(adPerScreenings, 10);

        if (selectedType === 'default') {
            this.setState({ loaderFlag: true });
            this.apiserve.sendevent('mobileAppConfiguration|updateConfig', {
                type: 'default',
                value: '',
                hubPage: selectedHubPage,
                adPerScreenings: adsVal,
            });
            this.saveTimeout = setTimeout(() => {
                this.setState({ loaderFlag: false });
                alertify.error('Save timed out. Please check your connection and try again.');
            }, 30000);
            return;
        }

        if (selectedType === 'image') {
            if (!imageFile) {
                this.setState({ loaderFlag: true });
                this.apiserve.sendevent('mobileAppConfiguration|updateConfig', {
                    type: 'image',
                    value: imagePreview || '',
                    hubPage: selectedHubPage,
                    adPerScreenings: adsVal,
                });
                this.saveTimeout = setTimeout(() => {
                    this.setState({ loaderFlag: false });
                    alertify.error('Save timed out. Please check your connection and try again.');
                }, 30000);
                return;
            }
            this.setState({ imageUploading: true });
            try {
                const ext = imageFile.name.split('.').pop();
                const fileName = `APP_HUBSCREEN_BACKGROUND_${selectedHubPage}.${ext}`;
                const s3Url = await commonImage(
                    imageFile,
                    false,
                    { street: `assest/${process.env.REACT_APP_ENVIRONMENT}` },
                    imagePreview,
                    fileName
                );
                if (!s3Url) throw new Error('Image upload failed. Please try again.');
                this.apiserve.sendevent('mobileAppConfiguration|updateConfig', {
                    type: 'image',
                    value: s3Url,
                    hubPage: selectedHubPage,
                    adPerScreenings: adsVal,
                });
                this.saveTimeout = setTimeout(() => {
                    this.setState({ loaderFlag: false, imageUploading: false });
                    alertify.error('Save timed out. Please check your connection and try again.');
                }, 30000);
            } catch (err) {
                this.setState({ imageUploading: false });
                alertify.error('Image upload failed. Please try again.');
            }
            return;
        }

        if (selectedType === 'color') {
            if (!HEX_REGEX.test(hexColor)) {
                this.setState({ hexError: 'Enter a valid hex color (e.g. #FF5733)' });
                return;
            }
            this.setState({ loaderFlag: true });
            this.apiserve.sendevent('mobileAppConfiguration|updateConfig', {
                type: 'color',
                value: hexColor,
                hubPage: selectedHubPage,
                adPerScreenings: adsVal,
            });
            this.saveTimeout = setTimeout(() => {
                this.setState({ loaderFlag: false, imageUploading: false });
                alertify.error('Save timed out. Please check your connection and try again.');
            }, 30000);
        }
    }

    handleReset() {
        const { configData, selectedHubPage, savedAdPerScreenings } = this.state;
        const entry = this.getPageEntry(configData, selectedHubPage);
        const pageState = this.extractPageState(entry);
        this.setState({
            ...pageState,
            adPerScreenings: savedAdPerScreenings,
            adPerScreeningsError: '',
        });
    }

    handleAdPerScreeningsChange(e) {
        const raw = e.target.value.replace(/[^0-9]/g, '');
        this.setState(prevState => {
            const next = { ...prevState, adPerScreenings: raw, adPerScreeningsError: '' };
            return { ...next, isDirty: this.checkDirty(next) };
        });
    }

    render() {
        const {
            selectedHubPage,
            selectedType,
            imagePreview,
            hexColor,
            hexError,
            loaderFlag,
            imageUploading,
            isDirty,
            adPerScreenings,
            adPerScreeningsError,
        } = this.state;

        const isSaving = loaderFlag || imageUploading;
        const colorPickerValue = HEX6_REGEX.test(hexColor) ? hexColor : '#000000';

        return (
            <div className="sd_app_config_page">
                <div className="sd_app_config_wrapper">
                   
                    <div className="sd_app_config_section">
                        <h3 className="sd_app_config_title">Hub Pages Background</h3>

                        <div className="sd_app_config_hub_select">
                            <label className="sd_field_label" htmlFor="hubPageSelect">Hub Page : </label>
                            <div className='select_field'>
                                <select
                                    id="hubPageSelect"
                                    className="sd_select_input"
                                    value={selectedHubPage}
                                    onChange={this.handleHubPageChange}
                                    disabled={isSaving}
                                >
                                    {HUB_PAGES_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="sd_app_config_radio_group">
                            <label className="sd_radio_label">
                                <input
                                    type="radio"
                                    name="backgroundType"
                                    value="image"
                                    checked={selectedType === 'image'}
                                    onChange={() => this.handleTypeChange('image')}
                                />
                                <span className="sd_radio_dot" />
                                Background Image
                            </label>
                            <label className="sd_radio_label">
                                <input
                                    type="radio"
                                    name="backgroundType"
                                    value="color"
                                    checked={selectedType === 'color'}
                                    onChange={() => this.handleTypeChange('color')}
                                />
                                <span className="sd_radio_dot" />
                                Hex Color
                            </label>
                            <label className="sd_radio_label">
                                <input
                                    type="radio"
                                    name="backgroundType"
                                    value="default"
                                    checked={selectedType === 'default'}
                                    onChange={() => this.handleTypeChange('default')}
                                />
                                <span className="sd_radio_dot" />
                                Default
                            </label>
                        </div>

                        {selectedType === 'image' && (
                            <div className="sd_app_config_image_section">
                                <div className="title_content_img">
                                    <div className="title_content_img_box sd_hub_img_box loader-main">
                                        <img
                                            src={imagePreview
                                                ? (imagePreview.startsWith('data:')
                                                    ? imagePreview
                                                    : `${this.apiserve.imageUrl}${imagePreview}?ts=${new Date().getTime()}`)
                                                : '/images/image-placeholder.png'}
                                            alt="Hub Background"
                                            className="title_content_image"
                                            onError={e => { e.target.src = '/images/image-placeholder.png'; }}
                                        />
                                        <UploadForm
                                            item={{ _id: 'hubBgUpload' }}
                                            handleImage={this.handleImageChange}
                                        />
                                        {imagePreview && (
                                            <span className="remove_file pointer" onClick={this.handleImageDelete}>
                                                <DeletefileIcon />
                                            </span>
                                        )}
                                    </div>
                                    <span className="error_note">Max 1 MB</span>
                                    <span className="error_note">Recommended: 390 × 844 PX</span>
                                </div>
                            </div>
                        )}

                        {selectedType === 'color' && (
                            <div className="sd_app_config_hex_section">
                                <div className="sd_hex_row">
                                    <input
                                        type="color"
                                        className="sd_color_picker"
                                        value={colorPickerValue}
                                        onChange={this.handleColorPickerChange}
                                    />
                                    <input
                                        type="text"
                                        placeholder="#FF5733"
                                        value={hexColor}
                                        onChange={this.handleHexChange}
                                        maxLength={7}
                                        className="sd_hex_input"
                                    />
                                </div>
                                {hexError && <p className="error_msg">{hexError}</p>}
                            </div>
                        )}

                        {selectedType === 'default' && (
                            <p className="sd_field_hint sd_default_hint">
                                App will display the original default background.
                            </p>
                        )}

                    </div>

                    <div className="sd_app_config_section sd_app_config_ads_section">
                        <h3 className="sd_app_config_title">Schedule Ads</h3>
                        <div className="sd_ads_field_row">
                            <label className="sd_field_label" htmlFor="adPerScreenings">
                                Ads Per Screening :
                            </label>
                            <input
                                id="adPerScreenings"
                                type="text"
                                inputMode="numeric"
                                className="sd_ads_number_input"
                                value={adPerScreenings}
                                onChange={this.handleAdPerScreeningsChange}
                                disabled={isSaving}
                                maxLength={2}
                            />
                        </div>
                        {adPerScreeningsError && (
                            <p className="error">{adPerScreeningsError}</p>
                        )}
                    </div>
                </div>

                <div className="text_center field_row w_100 buttons_outer">
                    <button
                        className="grayBTN uppercase"
                        onClick={this.handleReset}
                        disabled={isSaving}
                    >
                        Reset
                    </button>
                    <button
                        className={`${(isDirty && !isSaving) ? 'active' : ''} uppercase`}
                        onClick={this.handleSave}
                        disabled={!isDirty || isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        );
    }
}

export default AppConfigForm;
